import { Typography } from '@design-system/components/typography/typography';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { useCollectionSwapConfigQuery } from 'generated/graphql';
import { useIsCreatorApprovedSwapsEnabled } from '../model/useIsCreatorApprovedSwapsEnabled';
import * as styles from './ProgramSwapsToggle.css';

type ProgramSwapsToggleProps = {
  influencerId: number;
  // Absent while the program is not created yet - the engine config is keyed
  // by program id, so there is nothing to toggle before the first save.
  programId?: number;
  // The form's value; stays undefined until the user touches the toggle, so
  // an untouched save never writes the engine.
  value: boolean | undefined;
  onChange: (value: boolean) => void;
  disabled?: boolean;
};

// The stored value is read here (cache-first: the collection page selects
// collection.swapConfig behind its screen loader, so this resolves from the
// cache; page refetch after a save keeps it fresh) and only displayed - it
// enters the host form exclusively through a user click, which keeps
// dirty-tracking and payloads untouched until the creator flips the switch.
const LoadedToggle = ({
  influencerId,
  programId,
  value,
  onChange,
  disabled,
}: ProgramSwapsToggleProps & { programId: number }) => {
  const enabled = useIsCreatorApprovedSwapsEnabled(influencerId);
  const { data, loading } = useCollectionSwapConfigQuery({
    variables: { collectionId: programId },
    skip: !enabled,
  });
  if (!enabled) return null;

  const stored = data?.collection.swapConfig?.swapsEnabled;
  const shown = value ?? (typeof stored === 'boolean' ? stored : undefined);
  const unavailable = !loading && shown === undefined;
  // Toggle's onClick fires regardless of its disabled styling - withholding
  // the handler is what actually blocks the interaction.
  const handleChange = disabled || shown === undefined ? undefined : onChange;

  return (
    <div className={styles.field} data-testid="program-swaps-field">
      <div className={styles.row}>
        <Typography variant="body" size="large" color="N-100">
          Exercise modifications
        </Typography>
        <div data-testid="program-swaps-toggle">
          <Toggle
            value={shown === true}
            variant="primary"
            disabled={disabled || shown === undefined}
            onChange={handleChange}
          />
        </div>
      </div>
      <Typography variant="body" size="medium" color="N-80" fontWeight="normal">
        {unavailable
          ? 'Exercise modifications are temporarily unavailable. Reload the page to try again.'
          : 'Allow AI-suggested swaps for this program.'}
      </Typography>
    </div>
  );
};

export const ProgramSwapsToggle = (p: ProgramSwapsToggleProps) =>
  p.programId === undefined ? null : (
    <LoadedToggle {...p} programId={p.programId} />
  );
